import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge } from '@wearenotch/ui-react';
import { patterns, getPatternById } from '../../data';
import { Pattern, Category } from '../../types';
import { categoryColor, ifTypeBadgeVariant } from '../../utils/categoryColors';
import { CategoryBadge } from '../../components/CategoryBadge/CategoryBadge';
import { ComplexityIndicator } from '../../components/ComplexityIndicator/ComplexityIndicator';
import styles from './Compare.module.css';

// Canonical category order for the select grouping
const CATEGORY_ORDER: Category[] = [
  'Transparency',
  'Control',
  'Error Handling',
  'Onboarding',
  'Conversation',
];

// ── PatternSelect ─────────────────────────────────────────────────────────────

interface PatternSelectProps {
  value: string;
  onChange: (slug: string) => void;
  placeholder: string;
  grouped: Map<Category, Pattern[]>;
  excludeSlug: string;
  label: string;
}

function PatternSelect({ value, onChange, placeholder, grouped, excludeSlug, label }: PatternSelectProps) {
  return (
    <div className={styles.selectWrap}>
      <label className={styles.selectLabel}>{label}</label>
      <div className={styles.selectInner}>
        <select
          className={styles.select}
          value={value}
          onChange={e => onChange(e.target.value)}
          aria-label={placeholder}
        >
          <option value="">{placeholder}</option>
          {CATEGORY_ORDER.map(cat => {
            const options = (grouped.get(cat) ?? []).filter(p => p.id !== excludeSlug);
            if (options.length === 0) return null;
            return (
              <optgroup key={cat} label={cat}>
                {options.map(p => (
                  <option key={p.id} value={p.id}>{p.title}</option>
                ))}
              </optgroup>
            );
          })}
        </select>
        <svg
          className={styles.selectChevron}
          width="14" height="14" viewBox="0 0 24 24"
          fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
    </div>
  );
}

// ── Compare ───────────────────────────────────────────────────────────────────

export function Compare() {
  const [searchParams, setSearchParams] = useSearchParams();
  const aSlug = searchParams.get('a') ?? '';
  const bSlug = searchParams.get('b') ?? '';

  const patternA = aSlug ? getPatternById(aSlug) : undefined;
  const patternB = bSlug ? getPatternById(bSlug) : undefined;

  const grouped = useMemo(() => {
    const map = new Map<Category, Pattern[]>();
    for (const cat of CATEGORY_ORDER) map.set(cat, []);
    for (const p of patterns) {
      if (map.has(p.category as Category)) {
        map.get(p.category as Category)!.push(p);
      }
    }
    return map;
  }, []);

  function setSlug(param: 'a' | 'b', slug: string) {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (slug) next.set(param, slug); else next.delete(param);
      return next;
    });
  }

  function clear() {
    setSearchParams({});
  }

  const bothSelected = !!patternA && !!patternB;

  // ── Comparison rows ────────────────────────────────────────────────────────

  type RowDef = { label: string; render: (p: Pattern) => React.ReactNode };

  const rows: RowDef[] = [
    {
      label: 'Category',
      render: p => <CategoryBadge category={p.category} />,
    },
    {
      label: 'Interface type',
      render: p => (
        <div className={styles.badgeRow}>
          {p.interface_type.map(t => (
            <Badge key={t} variant={ifTypeBadgeVariant[t]} size="sm">{t}</Badge>
          ))}
        </div>
      ),
    },
    {
      label: 'Complexity',
      render: p => p.complexity
        ? <ComplexityIndicator complexity={p.complexity} />
        : <span className={styles.na}>—</span>,
    },
    {
      label: 'Problem',
      render: p => <p className={styles.bodyText}>{p.problem}</p>,
    },
    {
      label: 'When to use',
      render: p => <p className={styles.bodyText}>{p.when_to_use}</p>,
    },
    {
      label: 'Good example',
      render: p => {
        const summary = p.examples[0]?.good.summary;
        return summary
          ? <p className={`${styles.exampleText} ${styles.exampleGood}`}>{summary}</p>
          : <span className={styles.na}>—</span>;
      },
    },
    {
      label: 'Bad example',
      render: p => {
        const summary = p.examples[0]?.bad.summary;
        return summary
          ? <p className={`${styles.exampleText} ${styles.exampleBad}`}>{summary}</p>
          : <span className={styles.na}>—</span>;
      },
    },
    {
      label: 'Guidelines',
      render: p => (
        <ol className={styles.guidelineList}>
          {p.guidelines.map((g, i) => (
            <li key={i} className={styles.guidelineItem}>
              <span
                className={styles.guidelineNum}
                style={{ color: categoryColor[p.category as Category] }}
              >
                {i + 1}
              </span>
              <span>{g}</span>
            </li>
          ))}
        </ol>
      ),
    },
    {
      label: 'Related patterns',
      render: p => {
        const related = (p.related_patterns ?? [])
          .map(slug => getPatternById(slug))
          .filter((rp): rp is Pattern => rp !== undefined);
        return related.length > 0 ? (
          <div className={styles.relatedList}>
            {related.map(rp => (
              <Link key={rp.id} to={`/pattern/${rp.id}`} className={styles.relatedLink}>
                {rp.title}
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        ) : <span className={styles.na}>—</span>;
      },
    },
  ];

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <main className={styles.main}>

      {/* Hero */}
      <section className={styles.hero}>
        <div className="container">
          <div className={styles.heroTop}>
            <div>
              <h1 className={`${styles.title} rise`}>Compare Patterns</h1>
              <p className={`${styles.subtitle} page-intro`}>Select two patterns to see them side by side.</p>
            </div>
            {(patternA || patternB) && (
              <button className={styles.clearBtn} onClick={clear}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Clear
              </button>
            )}
          </div>

          <div className={styles.selectors}>
            <PatternSelect
              label="First pattern"
              value={aSlug}
              onChange={slug => setSlug('a', slug)}
              placeholder="Select first pattern"
              grouped={grouped}
              excludeSlug={bSlug}
            />
            <div className={styles.vsLabel} aria-hidden="true">vs</div>
            <PatternSelect
              label="Second pattern"
              value={bSlug}
              onChange={slug => setSlug('b', slug)}
              placeholder="Select second pattern"
              grouped={grouped}
              excludeSlug={aSlug}
            />
          </div>
        </div>
      </section>

      <div className="container">

        {/* Empty state */}
        {!bothSelected && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <line x1="9" y1="3" x2="9" y2="21" />
                <line x1="15" y1="3" x2="15" y2="21" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="3" y1="15" x2="21" y2="15" />
              </svg>
            </div>
            <p className={styles.emptyText}>
              {!aSlug && !bSlug
                ? 'Select two patterns above to compare them side by side.'
                : 'Select a second pattern to start comparing.'}
            </p>
          </div>
        )}

        {/* Comparison */}
        {bothSelected && (
          <div className={styles.comparison}>

            {/* Sticky column headers */}
            <div className={styles.stickyHeader}>
              <div className={styles.stickyHeaderLabel} />
              <div className={styles.stickyHeaderCell}>
                <Link to={`/pattern/${patternA.id}`} className={styles.patternNameLink}>
                  <span>{patternA.title}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </Link>
              </div>
              <div className={styles.stickyHeaderCell}>
                <Link to={`/pattern/${patternB.id}`} className={styles.patternNameLink}>
                  <span>{patternB.title}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </Link>
              </div>
            </div>

            {/* Data rows */}
            <div className={styles.table}>
              {rows.map((row, i) => (
                <div
                  key={row.label}
                  className={`${styles.row} ${i % 2 === 1 ? styles.rowAlt : ''}`}
                >
                  <div className={styles.rowLabel}>{row.label}</div>
                  <div className={styles.cell}>{row.render(patternA)}</div>
                  <div className={styles.cell}>{row.render(patternB)}</div>
                </div>
              ))}

              {/* Footer row */}
              <div className={`${styles.row} ${styles.footerRow}`}>
                <div className={styles.rowLabel} />
                <div className={styles.cell}>
                  <Link to={`/pattern/${patternA.id}`} className={styles.viewFullLink}>
                    View full pattern
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
                <div className={styles.cell}>
                  <Link to={`/pattern/${patternB.id}`} className={styles.viewFullLink}>
                    View full pattern
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </main>
  );
}
